import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usersAPI } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import {
  Search,
  Plus,
  ChevronRight,
  Mail,
  Users as UsersIcon,
  AlertCircle,
  ChevronLeft,
  Filter,
  ChevronsLeft,
  ChevronsRight,
  Baby,
  CreditCard,
  QrCode,
  Copy,
  Trash2,
} from "lucide-react";
import QRCode from "react-qr-code";
import { tempSignupUrl } from "../../lib/tempSignup";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const roleLabels = {
  OWNER: "Proprietar",
  COACH: "Antrenor",
  USER: "Utilizator",
};

const UsersPage = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users"); // "users" | "kids"
  const [users, setUsers] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kidsLoading, setKidsLoading] = useState(false);
  const [kidsSearch, setKidsSearch] = useState("");
  const [kidsSortBy, setKidsSortBy] = useState("name_asc");
  const [kidsPage, setKidsPage] = useState(1);
  const [kidsLimit, setKidsLimit] = useState(15);

  // Paginare & Filtre
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [roleFilter, setRoleFilter] = useState("");

  // Modale
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSignupQR, setShowSignupQR] = useState(false); // QR către pagina de înscriere self-service
  const [newChildren, setNewChildren] = useState([]);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    role: "USER",
    can_receive_subscriptions: false,
    secondary_parent_name: "",
    secondary_parent_phone: "",
  });
  const [creating, setCreating] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Dacă e selectat "problems_first", trimitem parametrul special backend-ului
      const isProblemsFirst = sortBy === "problems_first";

      const response = await usersAPI.getAll({
        page,
        limit,
        search,
        sort_by: isProblemsFirst ? "name_asc" : sortBy,
        filter_status: isProblemsFirst ? "problems" : undefined,
        role: roleFilter || undefined,
      });

      setUsers(response.data.data || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      toast.error("Eroare la încărcarea utilizatorilor");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, sortBy, roleFilter]);

  // Debounce pentru căutare
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 400);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const fetchChildren = useCallback(async () => {
    setKidsLoading(true);
    try {
      const res = await usersAPI.getAllChildren();
      setChildren(res.data);
    } catch {
      toast.error("Eroare la încărcarea copiilor");
    } finally {
      setKidsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "kids") fetchChildren();
  }, [activeTab, fetchChildren]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...newUser,
        email: newUser.email.trim() === "" ? null : newUser.email.trim(),
        phone: newUser.phone.trim() === "" ? null : newUser.phone.trim(),
        secondary_parent_name:
          newUser.secondary_parent_name.trim() === ""
            ? null
            : newUser.secondary_parent_name.trim(),
        secondary_parent_phone:
          newUser.secondary_parent_phone.trim() === ""
            ? null
            : newUser.secondary_parent_phone.trim(),
        children:
          newUser.role === "USER"
            ? newChildren
                .filter((c) => c.name.trim())
                .map((c) => ({
                  name: c.name.trim(),
                  birth_date: c.birth_date || null,
                }))
            : [],
      };

      const res = await usersAPI.create(payload);
      toast.success("Utilizator creat cu succes!");
      setShowCreateModal(false);
      setNewUser({
        name: "",
        email: "",
        phone: "",
        role: "USER",
        can_receive_subscriptions: false,
        secondary_parent_name: "",
        secondary_parent_phone: "",
      });
      setNewChildren([]);
      // Mergem direct pe fișa utilizatorului pentru a-i vinde abonamentele
      if (res.data?.id && payload.role === "USER") {
        navigate(`/admin/utilizatori/${res.data.id}`);
        return;
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la creare");
    } finally {
      setCreating(false);
    }
  };

  // Reset to page 1 when search or sort changes
  useEffect(() => {
    setKidsPage(1);
  }, [kidsSearch, kidsSortBy, kidsLimit]);

  const sortedChildren = useMemo(() => {
    const filtered = children.filter(
      (c) =>
        !kidsSearch ||
        c.name.toLowerCase().includes(kidsSearch.toLowerCase()) ||
        c.parent_name.toLowerCase().includes(kidsSearch.toLowerCase()),
    );
    const hasProblems = (c) => {
      if (!c.active_subscription) return true;
      const r = c.active_subscription.sessions_remaining;
      if (r != null && r <= 1) return true;
      const exp = c.active_subscription.expires_at;
      if (exp && new Date(exp) - new Date() < 7 * 24 * 60 * 60 * 1000)
        return true;
      return false;
    };
    const cmp = (a, b) => {
      switch (kidsSortBy) {
        case "name_desc":
          return b.name.localeCompare(a.name, "ro");
        case "youngest":
          return (b.birth_date || "").localeCompare(a.birth_date || "");
        case "oldest":
          return (a.birth_date || "9999").localeCompare(b.birth_date || "9999");
        case "problems_first": {
          const diff = (hasProblems(b) ? 1 : 0) - (hasProblems(a) ? 1 : 0);
          return diff !== 0 ? diff : a.name.localeCompare(b.name, "ro");
        }
        case "expiry_asc": {
          const ax = a.active_subscription?.expires_at || "9999";
          const bx = b.active_subscription?.expires_at || "9999";
          return ax.localeCompare(bx);
        }
        case "name_asc":
        default:
          return a.name.localeCompare(b.name, "ro");
      }
    };
    return [...filtered].sort(cmp);
  }, [children, kidsSearch, kidsSortBy]);

  const kidsTotalPages = Math.max(
    1,
    Math.ceil(sortedChildren.length / kidsLimit),
  );
  const pagedChildren = useMemo(
    () =>
      sortedChildren.slice((kidsPage - 1) * kidsLimit, kidsPage * kidsLimit),
    [sortedChildren, kidsPage, kidsLimit],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">
            Utilizatori
          </h1>
          <p className="text-white/40 text-base mt-1">
            Gestionează clienții și personalul ({totalUsers} total)
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowSignupQR(true)}
            className="flex-1 sm:flex-none justify-center px-4 py-2.5 flex items-center gap-2 text-sm font-bold uppercase tracking-widest bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white transition-colors rounded"
            title="Cod QR pentru pagina de înscriere (testare)"
          >
            <QrCode size={16} /> QR Înscriere
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex-1 sm:flex-none justify-center px-4 py-2.5 flex items-center gap-2 text-sm font-bold uppercase tracking-widest"
          >
            <Plus size={16} /> Adaugă Utilizator
          </button>
        </div>
      </div>

      {/* Tab Switch */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab("users")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-black uppercase tracking-widest transition-colors rounded ${
            activeTab === "users"
              ? "bg-[#6db025] text-black"
              : "bg-white/5 text-white/40 hover:bg-white/10"
          }`}
        >
          <UsersIcon size={14} /> Adulți / Părinți
        </button>
        <button
          onClick={() => setActiveTab("kids")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-black uppercase tracking-widest transition-colors rounded ${
            activeTab === "kids"
              ? "bg-[#6db025] text-black"
              : "bg-white/5 text-white/40 hover:bg-white/10"
          }`}
        >
          <Baby size={14} /> Copii ({children.length || "…"})
        </button>
      </div>

      {/* Kids View */}
      {activeTab === "kids" && (
        <div className="space-y-4">
          {/* Bara de Filtre */}
          <div className="bg-[#0A0A0A] border border-white/5 p-4 rounded flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                size={16}
              />
              <input
                type="text"
                value={kidsSearch}
                onChange={(e) => setKidsSearch(e.target.value)}
                placeholder="Caută după numele copilului sau al părintelui..."
                className="w-full bg-[#121212] border border-white/10 pl-11 pr-4 py-2.5 rounded text-white focus:border-[#6db025] outline-none text-base placeholder:text-white/20 transition-colors"
              />
            </div>

            <select
              value={kidsSortBy}
              onChange={(e) => setKidsSortBy(e.target.value)}
              className="bg-[#121212] border border-white/10 text-white/60 text-sm font-bold uppercase tracking-wider py-2.5 px-3 rounded outline-none cursor-pointer focus:border-[#6db025] transition-colors"
            >
              <option value="name_asc" className="bg-[#121212] text-white">
                A-Z (Nume)
              </option>
              <option value="name_desc" className="bg-[#121212] text-white">
                Z-A (Nume)
              </option>
              <option value="youngest" className="bg-[#121212] text-white">
                Cei mai tineri
              </option>
              <option value="oldest" className="bg-[#121212] text-white">
                Cei mai în vârstă
              </option>
              <option
                value="problems_first"
                className="bg-[#121212] text-white"
              >
                Cu probleme primii
              </option>
              <option value="expiry_asc" className="bg-[#121212] text-white">
                Expirare abonament
              </option>
            </select>
          </div>

          {kidsLoading ? (
            <div className="bg-[#0A0A0A] border border-white/5 p-16 text-center text-[#6db025] animate-pulse font-bold tracking-widest text-sm uppercase rounded">
              Se preiau datele...
            </div>
          ) : pagedChildren.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-white/5 p-16 text-center flex flex-col items-center gap-4 rounded">
              <Baby size={40} className="text-white/10" />
              <p className="text-white/40 text-base font-bold uppercase tracking-widest">
                Niciun copil găsit
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pagedChildren.map((child) => (
                <button
                  key={child.id}
                  onClick={() =>
                    navigate(`/admin/utilizatori/${child.parent_user_id}`)
                  }
                  className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden group text-left"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-purple-400" />
                  <div className="p-4 pl-5 flex items-start gap-3">
                    <div className="w-11 h-11 bg-purple-500/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/20 transition-colors">
                      <Baby size={20} className="text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-base uppercase tracking-tight truncate">
                        {child.name}
                      </p>
                      <p className="text-white/50 text-xs uppercase font-black tracking-widest mt-1 truncate flex items-center gap-1.5">
                        <UsersIcon size={12} /> {child.parent_name}
                      </p>
                      {child.active_subscription ? (
                        <div className="mt-2 space-y-1">
                          <p className="text-[#6db025] text-xs font-black uppercase tracking-widest flex items-center gap-1.5 truncate">
                            <CreditCard size={12} />{" "}
                            {child.active_subscription.plan_name}
                          </p>
                          {child.active_subscription.sessions_total ? (
                            <p
                              className={`text-xs font-black uppercase tracking-widest ${
                                child.active_subscription.sessions_remaining <=
                                1
                                  ? "text-red-400"
                                  : child.active_subscription
                                        .sessions_remaining <= 3
                                    ? "text-yellow-400"
                                    : "text-white/50"
                              }`}
                            >
                              {child.active_subscription.sessions_remaining}/
                              {child.active_subscription.sessions_total} ședințe
                            </p>
                          ) : child.active_subscription.expires_at ? (
                            <p className="text-xs text-white/40 font-bold uppercase tracking-widest">
                              exp.{" "}
                              {format(
                                new Date(child.active_subscription.expires_at),
                                "dd MMM yyyy",
                                { locale: ro },
                              )}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <p className="text-xs text-white/30 font-bold uppercase tracking-widest mt-2">
                          Fără abonament activ
                        </p>
                      )}
                    </div>
                    <ChevronRight
                      size={18}
                      className="text-white/20 group-hover:text-purple-400 transition-colors flex-shrink-0"
                    />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Paginare Copii */}
          {sortedChildren.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between bg-[#0A0A0A] border border-white/5 p-4 rounded gap-4">
              <div className="text-white/50 text-sm font-bold uppercase tracking-wider">
                Pagina <span className="text-white">{kidsPage}</span> din{" "}
                <span className="text-white">{kidsTotalPages}</span>
                <span className="text-white/30 ml-2">
                  · {sortedChildren.length} copii
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setKidsPage(1)}
                  disabled={kidsPage === 1}
                  className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <ChevronsLeft size={18} />
                </button>
                <button
                  onClick={() => setKidsPage((p) => Math.max(1, p - 1))}
                  disabled={kidsPage === 1}
                  className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>

                <div className="flex items-center bg-[#121212] border border-white/10 rounded px-2 mx-1">
                  <select
                    value={kidsLimit}
                    onChange={(e) => setKidsLimit(Number(e.target.value))}
                    className="bg-[#121212] text-white/60 text-sm font-bold uppercase tracking-wider outline-none cursor-pointer py-2"
                  >
                    <option value="15" className="bg-[#121212] text-white">
                      15 / pag
                    </option>
                    <option value="30" className="bg-[#121212] text-white">
                      30 / pag
                    </option>
                    <option value="50" className="bg-[#121212] text-white">
                      50 / pag
                    </option>
                  </select>
                </div>

                <button
                  onClick={() =>
                    setKidsPage((p) => Math.min(kidsTotalPages, p + 1))
                  }
                  disabled={kidsPage === kidsTotalPages}
                  className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
                <button
                  onClick={() => setKidsPage(kidsTotalPages)}
                  disabled={kidsPage === kidsTotalPages}
                  className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                >
                  <ChevronsRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Adulți view (hidden when kids tab active) */}
      {
        activeTab === "users" && (
          <>
            {/* Bara de Filtre */}
            <div className="bg-[#0A0A0A] border border-white/5 p-4 rounded flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                  size={16}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Caută după nume sau email..."
                  className="w-full bg-[#121212] border border-white/10 pl-11 pr-4 py-2.5 rounded text-white focus:border-[#6db025] outline-none transition-colors text-base placeholder:text-white/20"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 bg-[#121212] border border-white/10 rounded px-3 focus-within:border-[#6db025] transition-colors">
                  <Filter size={14} className="text-white/40" />
                  <select
                    value={roleFilter}
                    onChange={(e) => {
                      setRoleFilter(e.target.value);
                      setPage(1);
                    }}
                    className="bg-[#121212] text-white/60 text-sm font-bold uppercase tracking-wider py-2.5 outline-none cursor-pointer"
                  >
                    <option value="" className="bg-[#121212] text-white">
                      Toate Rolurile
                    </option>
                    <option value="USER" className="bg-[#121212] text-white">
                      Clienți
                    </option>
                    <option value="COACH" className="bg-[#121212] text-white">
                      Antrenori
                    </option>
                    <option value="OWNER" className="bg-[#121212] text-white">
                      Administratori
                    </option>
                  </select>
                </div>

                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  className="bg-[#121212] border border-white/10 text-white/60 text-sm font-bold uppercase tracking-wider py-2.5 px-3 rounded outline-none cursor-pointer focus:border-[#6db025] transition-colors"
                >
                  <option value="name_asc" className="bg-[#121212] text-white">
                    A-Z (Nume)
                  </option>
                  <option value="name_desc" className="bg-[#121212] text-white">
                    Z-A (Nume)
                  </option>
                  <option value="newest" className="bg-[#121212] text-white">
                    Cei mai noi
                  </option>
                  <option
                    value="problems_first"
                    className="bg-[#121212] text-white"
                  >
                    Cu probleme primii
                  </option>
                  <option
                    value="subs_count_desc"
                    className="bg-[#121212] text-white"
                  >
                    Mai multe abonamente
                  </option>
                  <option
                    value="finalization_desc"
                    className="bg-[#121212] text-white"
                  >
                    Rată finalizare (desc)
                  </option>
                  <option
                    value="expiry_asc"
                    className="bg-[#121212] text-white"
                  >
                    Expirare abonament
                  </option>
                </select>
              </div>
            </div>

            {/* Lista de Utilizatori */}
            {loading && users.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-white/5 p-16 text-center text-[#6db025] animate-pulse font-bold tracking-widest text-sm uppercase rounded">
                Se preiau datele...
              </div>
            ) : users.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-white/5 p-16 text-center flex flex-col items-center gap-4 rounded">
                <AlertCircle size={40} className="text-white/10" />
                <p className="text-white/40 text-base font-bold uppercase tracking-widest">
                  Niciun utilizator găsit
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {users.map((u) => {
                  const stripeColor =
                    u.status_color === "red"
                      ? "bg-red-500"
                      : u.status_color === "yellow"
                        ? "bg-yellow-500"
                        : "bg-[#6db025]";
                  return (
                    <Link
                      key={u.id}
                      to={`/admin/utilizatori/${u.id}`}
                      className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden group"
                    >
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-[3px] ${stripeColor}`}
                      />
                      <div className="p-4 pl-5 flex items-start gap-3">
                        <div className="relative w-11 h-11 bg-[#6db025]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#6db025]/20 transition-colors">
                          <span className="text-[#6db025] font-bold uppercase">
                            {u.name?.charAt(0) || "U"}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <p className="text-white font-bold text-base uppercase tracking-tight truncate">
                              {u.name}
                            </p>
                            {u.role !== "USER" && (
                              <span
                                className={`text-[10px] uppercase px-1.5 py-0.5 rounded font-black tracking-widest flex-shrink-0 ${
                                  u.role === "OWNER"
                                    ? "bg-purple-500/20 text-purple-400"
                                    : "bg-blue-500/20 text-blue-400"
                                }`}
                              >
                                {roleLabels[u.role]}
                              </span>
                            )}
                            {u.can_receive_subscriptions && (
                              <span className="text-[10px] uppercase px-1.5 py-0.5 bg-[#6db025]/10 text-[#6db025] font-black tracking-widest rounded flex-shrink-0">
                                Participant
                              </span>
                            )}
                            {u.marked_for_deletion && (
                              <span className="text-[10px] uppercase px-1.5 py-0.5 bg-red-500/20 text-red-400 font-black tracking-widest rounded flex-shrink-0">
                                Inactiv
                              </span>
                            )}
                          </div>
                          <p className="text-white/50 text-xs uppercase font-medium tracking-wide truncate flex items-center gap-1.5">
                            <Mail
                              size={12}
                              className={u.email ? "" : "opacity-40"}
                            />
                            {u.email || "Fără email"}
                          </p>
                          {u.children?.length > 0 && (
                            <p className="text-[#6db025]/70 text-xs uppercase font-black tracking-widest mt-1 flex items-center gap-1.5">
                              <UsersIcon size={12} /> {u.children.length} copii
                            </p>
                          )}
                        </div>
                        <ChevronRight
                          size={18}
                          className="text-white/20 group-hover:text-[#6db025] transition-colors flex-shrink-0"
                        />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Controale de Paginare Komplexe */}
            {totalPages > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between bg-[#0A0A0A] border border-white/5 p-4 rounded gap-4">
                <div className="text-white/50 text-sm font-bold uppercase tracking-wider">
                  Pagina <span className="text-white">{page}</span> din{" "}
                  <span className="text-white">{totalPages}</span>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <ChevronsLeft size={18} />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <div className="flex items-center bg-[#121212] border border-white/10 rounded px-2 mx-1">
                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      className="bg-[#121212] text-white/60 text-sm font-bold uppercase tracking-wider outline-none cursor-pointer py-2"
                    >
                      <option value="15" className="bg-[#121212] text-white">
                        15 / pag
                      </option>
                      <option value="30" className="bg-[#121212] text-white">
                        30 / pag
                      </option>
                      <option value="50" className="bg-[#121212] text-white">
                        50 / pag
                      </option>
                    </select>
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages}
                    className="p-2 rounded bg-white/5 hover:bg-white/10 text-white/60 hover:text-white disabled:opacity-30 transition-colors"
                  >
                    <ChevronsRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) /* end adults tab */
      }

      {/* Modal Adaugare Utilizator */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#121212] border-white/10 w-[calc(100vw-1.5rem)] max-w-md rounded-xl p-0 shadow-2xl max-h-[92dvh] flex flex-col overflow-hidden gap-0">
          <DialogHeader className="p-4 sm:p-6 border-b border-white/5 flex-shrink-0">
            <DialogTitle className="text-white uppercase font-heading text-xl">
              Adaugă Utilizator
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={handleCreate}
            className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1"
          >
            <div>
              <label className="text-white/40 text-[11px] font-black uppercase tracking-widest mb-1.5 block">
                Nume Complet
              </label>
              <input
                type="text"
                placeholder="Ex: Ion Popescu"
                required
                value={newUser.name}
                onChange={(e) =>
                  setNewUser({ ...newUser, name: e.target.value })
                }
                className="w-full bg-[#0A0A0A] border border-white/10 rounded p-4 text-white focus:border-[#6db025] outline-none transition-colors"
              />
            </div>

            <div>
              <label className="text-white/40 text-[11px] font-black uppercase tracking-widest mb-1.5 block">
                Adresă Email (Opțional)
              </label>
              <input
                type="email"
                placeholder="Fără email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                className="w-full bg-[#0A0A0A] border border-white/10 rounded p-4 text-white focus:border-[#6db025] outline-none transition-colors placeholder:text-white/20"
              />
            </div>

            <div>
              <label className="text-white/40 text-[11px] font-black uppercase tracking-widest mb-1.5 block">
                Telefon (Opțional)
              </label>
              <input
                type="text"
                placeholder="Fără telefon"
                value={newUser.phone}
                onChange={(e) =>
                  setNewUser({ ...newUser, phone: e.target.value })
                }
                className="w-full bg-[#0A0A0A] border border-white/10 rounded p-4 text-white focus:border-[#6db025] outline-none transition-colors placeholder:text-white/20"
              />
            </div>

            {isOwner && (
              <div>
                <label className="text-white/40 text-[11px] font-black uppercase tracking-widest mb-1.5 block">
                  Rol în sistem
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({ ...newUser, role: e.target.value })
                  }
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded p-4 text-white focus:border-[#6db025] outline-none transition-colors"
                >
                  <option value="USER">Client (Abonat)</option>
                  <option value="COACH">Antrenor</option>
                  <option value="OWNER">Administrator</option>
                </select>
              </div>
            )}

            {/* Opțiuni client */}
            {newUser.role === "USER" && (
              <div className="space-y-3 pt-2 border-t border-white/5">
                {/* Copii - adăugați direct la creare */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-white/30 text-[11px] uppercase font-bold tracking-widest flex items-center gap-1.5">
                      <Baby size={13} className="text-purple-400" /> Copii (
                      {newChildren.length})
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setNewChildren((prev) => [
                          ...prev,
                          { name: "", birth_date: "" },
                        ])
                      }
                      className="flex items-center gap-1 text-[#6db025] text-[11px] font-black uppercase tracking-widest px-2 py-1.5 bg-[#6db025]/10 border border-[#6db025]/20 rounded hover:bg-[#6db025]/20 transition-colors"
                    >
                      <Plus size={12} /> Adaugă copil
                    </button>
                  </div>
                  {newChildren.map((child, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-black/30 border border-white/5 rounded space-y-2"
                    >
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Numele copilului"
                          value={child.name}
                          onChange={(e) =>
                            setNewChildren((prev) =>
                              prev.map((c, i) =>
                                i === idx ? { ...c, name: e.target.value } : c,
                              ),
                            )
                          }
                          className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded p-3 text-white focus:border-[#6db025] outline-none transition-colors placeholder:text-white/20 text-base"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setNewChildren((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          className="px-3 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded flex-shrink-0"
                          aria-label="Șterge copilul"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <input
                        type="date"
                        value={child.birth_date}
                        onChange={(e) =>
                          setNewChildren((prev) =>
                            prev.map((c, i) =>
                              i === idx
                                ? { ...c, birth_date: e.target.value }
                                : c,
                            ),
                          )
                        }
                        title="Data nașterii (opțional)"
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded p-3 text-white focus:border-[#6db025] outline-none transition-colors text-base"
                      />
                    </div>
                  ))}
                </div>

                <label className="flex items-center gap-3 p-4 bg-white/[0.02] border border-white/5 rounded cursor-pointer hover:bg-white/[0.04] transition-colors">
                  <input
                    type="checkbox"
                    checked={newUser.can_receive_subscriptions}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        can_receive_subscriptions: e.target.checked,
                      })
                    }
                    className="w-4 h-4 accent-[#6db025]"
                  />
                  <div>
                    <p className="text-white text-base font-bold">
                      Poate lua abonamente
                    </p>
                    <p className="text-white/40 text-[11px] uppercase font-bold tracking-widest mt-0.5">
                      Adultul participă activ la cursuri
                    </p>
                  </div>
                </label>

                <div className="space-y-3">
                  <p className="text-white/30 text-[11px] uppercase font-bold tracking-widest">
                    Al doilea părinte / partener (opțional)
                  </p>
                  <input
                    type="text"
                    placeholder="Nume părinte 2"
                    value={newUser.secondary_parent_name}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        secondary_parent_name: e.target.value,
                      })
                    }
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded p-3 text-white focus:border-[#6db025] outline-none transition-colors placeholder:text-white/20 text-base"
                  />
                  <input
                    type="text"
                    placeholder="Telefon părinte 2 (opțional)"
                    value={newUser.secondary_parent_phone}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        secondary_parent_phone: e.target.value,
                      })
                    }
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded p-3 text-white focus:border-[#6db025] outline-none transition-colors placeholder:text-white/20 text-base"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={creating}
              className="w-full bg-[#6db025] hover:bg-opacity-90 text-white py-4 rounded uppercase font-black tracking-widest text-sm disabled:opacity-50 mt-6 transition-colors"
            >
              {creating
                ? "Se creează..."
                : newUser.role === "USER"
                  ? "Creează și deschide fișa"
                  : "Confirmă Crearea"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR către pagina de înscriere self-service */}
      <Dialog open={showSignupQR} onOpenChange={setShowSignupQR}>
        <DialogContent className="bg-[#121212] border-white/10 w-[calc(100vw-1.5rem)] max-w-sm rounded-xl p-0 shadow-2xl overflow-hidden gap-0">
          <DialogHeader className="p-5 border-b border-white/5">
            <DialogTitle className="text-white uppercase font-heading text-lg">
              Înscriere Cont Nou
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 flex flex-col items-center gap-4">
            <p className="text-white/40 text-sm text-center">
              Părintele scanează acest cod și își creează singur contul, parola
              și copiii.
            </p>
            <div className="bg-white p-4 rounded-xl border-4 border-[#6db025]">
              <QRCode
                value={tempSignupUrl()}
                size={220}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
              />
            </div>
            <div className="w-full bg-[#050505] border border-white/10 p-1 pl-3 flex items-center justify-between gap-2 rounded">
              <code className="text-[#6db025] text-[11px] font-mono truncate flex-1 opacity-80">
                {tempSignupUrl()}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(tempSignupUrl());
                  toast.success("Link copiat!");
                }}
                className="bg-white/5 hover:bg-[#6db025] text-white/40 hover:text-black p-3 transition-colors rounded"
                title="Copiază link-ul"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
